import { format, isToday, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { createSelector } from "reselect";

import reducerRegistry from "../../util/reducerRegistry";
import {
  REDUCER_NAME,
  RESET_COMPETITIONCUP,
  SET_COMPETITIONCUP,
  SET_ERROR,
  STATUS_POSTPONED,
} from "./constants";

const initialState = {
  isReady: false,
  isError: false,
  cup: [],
};

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_COMPETITIONCUP:
      return {
        ...state,
        isReady: true,
        isError: false,
        cup: action.data,
      };
    case RESET_COMPETITIONCUP:
      return initialState;
    case SET_ERROR:
      return {
        ...state,
        isReady: false,
        isError: true,
      };
    default:
      return state;
  }
};

const getState = (state) => {
  if (state[REDUCER_NAME]) {
    return state[REDUCER_NAME];
  }
  return initialState;
};

const toFormattedDate = (date) => {
  if (!date) {
    return date;
  }

  const dateObj = parseISO(date);
  if (isToday(dateObj)) {
    return `Today at ${format(
      utcToZonedTime(dateObj, "Europe/Paris"),
      "HH:mm"
    )}`;
  }
  return format(utcToZonedTime(dateObj, "Europe/Paris"), "E dd MMM HH:mm");
};

export const selectIsReady = createSelector(getState, (state) => state.isReady);

export const selectIsError = createSelector(getState, (state) => state.isError);

export const selectCompetitionCup = createSelector(getState, (state) => {
  // Track the desired order for each match
  const desiredOrder = new Map();

  // First pass: process from latest to earliest round to determine ordering
  const tempGrouped = state.cup.reduce((acc, match) => {
    (acc[match.gameWeek] = acc[match.gameWeek] || []).push(match);
    return acc;
  }, {});

  // Sort gameWeeks from latest to earliest (backwards)
  const sortedGameWeeks = Object.keys(tempGrouped).sort(
    (a, b) => Number(b) - Number(a)
  );

  // Process rounds from latest to earliest to assign order to parents
  sortedGameWeeks.forEach((gameWeek) => {
    const round = tempGrouped[gameWeek];

    // Sort this round by current order or desired order from children
    round.sort((a, b) => {
      const orderA = desiredOrder.get(a.id) ?? a.order ?? 0;
      const orderB = desiredOrder.get(b.id) ?? b.order ?? 0;
      return orderA - orderB;
    });

    // Set desired order for parent matches
    round.forEach((match, index) => {
      if (match.parentMatches && match.parentMatches.length > 0) {
        match.parentMatches.forEach((parent, parentIndex) => {
          const parentOrder = index * 10 + parentIndex;
          if (!desiredOrder.has(parent.id)) {
            desiredOrder.set(parent.id, parentOrder);
          }
        });
      }
    });
  });

  // Second pass: create final grouped structure with formatted data and proper sorting
  return state.cup
    .sort((a, b) => {
      // First sort by gameWeek
      if (a.gameWeek !== b.gameWeek) {
        return a.gameWeek - b.gameWeek;
      }
      // Then by desired order (or original order)
      const orderA = desiredOrder.get(a.id) ?? a.order ?? 0;
      const orderB = desiredOrder.get(b.id) ?? b.order ?? 0;
      return orderA - orderB;
    })
    .reduce((acc, n) => {
      (acc[n.gameWeek] = acc[n.gameWeek] || []).push({
        ...n,
        formattedStartDate: toFormattedDate(n.startDate),
        isEnded:
          !n.isLive &&
          parseISO(n.startDate) < new Date() &&
          n.status !== STATUS_POSTPONED,
      });
      return acc;
    }, {});
});

reducerRegistry.register(REDUCER_NAME, reducer);
